/**
 * Sistema de Cotización con Persistencia tipo Carrito
 * Similar al carrito de compras pero para solicitudes de cotización
 */

class QuotationCart {
  constructor() {
    this.storageKey = "copargo_quotation_cart";
    this.cart = this.loadCart();
    this.init();
  }

  // Inicializar el sistema
  init() {
    this.updateUI();
    this.bindEvents();

    // Escuchar cambios en otras pestañas
    window.addEventListener("storage", (e) => {
      if (e.key === this.storageKey) {
        this.cart = this.loadCart();
        this.updateUI();
      }
    });
  }

  // Cargar carrito desde localStorage
  loadCart() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Error loading quotation cart:", error);
      return [];
    }
  }

  // Guardar carrito en localStorage
  saveCart() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.cart));
      this.updateUI();

      // Disparar evento para otras pestañas
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: this.storageKey,
          newValue: JSON.stringify(this.cart),
        }),
      );
    } catch (error) {
      console.error("Error saving quotation cart:", error);
    }
  }

  // Agregar producto al carrito de cotización
  addProduct(product) {
    console.log("🛒 addProduct() ejecutándose...");
    console.log("📦 Producto recibido:", product);
    console.log("📋 Carrito antes de agregar:", this.cart);

    // Verificar si el producto ya está en el carrito
    const existingIndex = this.cart.findIndex((item) => item.id === product.id);
    console.log("🔍 Índice del producto existente:", existingIndex);

    if (existingIndex !== -1) {
      // Si ya existe, incrementar la cantidad o establecerla
      console.log(
        "📋 Producto ya existe en el carrito, actualizando cantidad...",
      );
      const qtyToAdd = product.quantity ? parseInt(product.quantity) : 1;
      this.cart[existingIndex].quantity += qtyToAdd;
      this.saveCart();
      this.showNotification(
        "Cantidad actualizada en la lista de cotización",
        "success",
      );
      // openModal se llamará desde el click handler
      return true;
    }

    // Agregar nuevo producto
    const qty = product.quantity ? parseInt(product.quantity) : 1;
    const cartItem = {
      id: product.id,
      title: product.title,
      variant_title: product.variant_title || "",
      handle: product.handle,
      url: product.url,
      price: product.price,
      compare_at_price: product.compare_at_price,
      featured_image: product.featured_image,
      vendor: product.vendor,
      type: product.type,
      tags: product.tags,
      added_at: new Date().toISOString(),
      quantity: qty,
      sku: product.sku || "",
    };

    console.log("📦 Nuevo item creado:", cartItem);

    this.cart.push(cartItem);
    console.log("📋 Carrito después de agregar:", this.cart);

    this.saveCart();
    console.log("💾 Carrito guardado en localStorage");

    this.showNotification(
      "Producto agregado a la lista de cotización",
      "success",
    );

    console.log("✅ Producto agregado exitosamente");
    return true;
  }

  // Eliminar producto del carrito
  removeProduct(productId) {
    console.log("🗑️ Eliminando producto:", productId);
    console.log("📦 Carrito actual:", this.cart);
    console.log(
      "🆔 IDs en carrito:",
      this.cart.map((item) => ({ id: item.id, type: typeof item.id })),
    );

    // Corregir comparación para que funcione con ambos string y number
    const index = this.cart.findIndex(
      (item) =>
        item.id == productId || // Comparación flexible (== en lugar de ===)
        item.id.toString() === productId.toString() ||
        String(item.id) === String(productId),
    );
    console.log("🔍 Índice encontrado:", index);

    if (index !== -1) {
      this.cart.splice(index, 1);
      this.saveCart();
      this.updateModal();
      this.showNotification(
        "Producto eliminado de la lista de cotización",
        "info",
      );
      console.log("✅ Producto eliminado correctamente");
      return true;
    } else {
      console.log("❌ Producto no encontrado para eliminar");
    }
    return false;
  }

  // Actualizar cantidad de producto
  updateQuantity(productId, quantity) {
    console.log("🔧 Actualizando cantidad:", productId, "a", quantity);
    console.log("📦 Carrito actual:", this.cart);
    console.log(
      "🆔 IDs en carrito:",
      this.cart.map((item) => ({ id: item.id, type: typeof item.id })),
    );

    const parsedQuantity = parseInt(quantity, 10);
    // Corregir comparación para que funcione con ambos string y number
    const item = this.cart.find(
      (item) =>
        item.id == productId || // Comparación flexible (== en lugar de ===)
        item.id.toString() === productId.toString() ||
        String(item.id) === String(productId),
    );

    console.log("🔍 Item encontrado:", item);

    if (item && !isNaN(parsedQuantity) && parsedQuantity > 0) {
      item.quantity = parsedQuantity;
      this.saveCart();
      this.updateModal();
      console.log("✅ Cantidad actualizada correctamente");
      return true;
    } else {
      console.log(
        "❌ Error actualizando cantidad - item no encontrado o cantidad inválida",
      );
    }
    return false;
  }

  // Vaciar carrito completo
  clearCart(silent) {
    this.cart = [];
    this.saveCart();
    this.updateUI();
    this.closeModal();
    if (!silent) {
      this.showNotification("Lista de cotización vaciada", "info");
    }
  }

  // Obtener total de productos
  getTotalItems() {
    return this.cart.reduce((total, item) => total + item.quantity, 0);
  }

  // Obtener resumen para el formulario
  getCartSummary() {
    return {
      items: this.cart,
      total_items: this.getTotalItems(),
      total_value: this.cart.reduce((total, item) => {
        const price = parseFloat(item.price) || 0;
        return total + price * item.quantity;
      }, 0),
      currency: "CLP",
    };
  }

  // Actualizar UI del botón y contadores
  updateUI() {
    const totalItems = this.getTotalItems();

    // Actualizar todos los botones de cotización (SIN BADGE)
    document.querySelectorAll(".cotizacion-btn-main").forEach((btn) => {
      // Eliminar badge si existe
      const badge = btn.querySelector(".quotation-badge");
      if (badge) {
        badge.remove();
      }

      // Obtener los datos del producto desde el botón
      const productData = this.getProductDataFromButton(btn);
      const isProductInCart =
        productData &&
        productData.id &&
        this.cart.some((item) => String(item.id) === String(productData.id));

      // Actualizar estado visual y texto según el estado del producto
      if (isProductInCart) {
        btn.classList.add("has-items");
        btn.querySelector("span").textContent = "Ver cotización";
      } else {
        btn.classList.remove("has-items");
        btn.querySelector("span").textContent = "Cotizar";
      }
    });

    // Actualizar modal si está abierto
    this.updateModal();
  }

  // Obtener ID del producto desde el botón
  getProductIdFromButton(button) {
    try {
      // Intentar obtener del atributo onclick
      const onclick = button.getAttribute("onclick");
      if (onclick && onclick.includes("quotationCart.addProduct")) {
        // Extraer el JSON del onclick
        const match = onclick.match(/quotationCart\.addProduct\((.*)\)/);
        if (match && match[1]) {
          // Evaluar el JSON de forma segura
          const productJson = match[1].replace(/'/g, '"');
          const product = JSON.parse(productJson);
          return product.id;
        }
      }

      // Si no funciona, intentar obtener de los datos del producto en la página
      return this.getProductIdFromPage(button);
    } catch (error) {
      console.error("Error getting product ID from button:", error);
      return null;
    }
  }

  // Obtener ID del producto desde la página
  getProductIdFromPage(button) {
    // Buscar la tarjeta de producto más cercana
    const card = button.closest(".hdt-card-product");
    if (!card) return null;

    // Generar ID consistente con el método existente
    return this.generateProductId(card);
  }

  // Actualizar contenido del modal
  updateModal() {
    console.log("🔄 updateModal() ejecutándose...");
    console.log("📦 Carrito actual:", this.cart);
    console.log("📋 Total items:", this.getTotalItems());

    const modalContent = document.querySelector(".quotation-modal-content");
    console.log("📋 Modal content encontrado:", modalContent ? "✅" : "❌");

    if (!modalContent) {
      console.log("❌ No se encontró .quotation-modal-content");
      return;
    }

    const summary = this.getCartSummary();
    console.log("📦 Resumen para modal:", summary);

    if (summary.items.length === 0) {
      console.log("📋 Carrito vacío, mostrando mensaje vacío");
      modalContent.innerHTML = `
        <div class="quotation-empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 2L3 9v11a2 2 0 002 2h14a2 2 0 002-2V9l-6-7z"/>
            <line x1="3" y1="9" x2="21" y2="9"/>
            <path d="M9 22V12h6v10"/>
          </svg>
          <h3>Tu lista de cotización está vacía</h3>
          <p>Agrega productos para solicitar una cotización</p>
          <button type="button" class="quotation-close-btn" onclick="quotationCart.closeModal()">
            Cerrar
          </button>
        </div>
      `;
    } else {
      console.log("📋 Carrito con productos, generando lista...");
      const itemsHTML = summary.items
        .map((item) => this.renderCartItem(item))
        .join("");
      console.log("✅ HTML de items generado:", itemsHTML.length, "caracteres");

      modalContent.innerHTML = `
        <div class="quotation-items">
          <div class="quotation-header">
            <h3>Productos para cotizar (${summary.total_items})</h3>
            <button type="button" class="quotation-close-btn" onclick="quotationCart.closeModal()">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="quotation-list">
            ${itemsHTML}
          </div>
          <div class="quotation-actions">
            <button type="button" class="quotation-continue-btn" onclick="quotationCart.closeModal()">
              Seguir cotizando
            </button>
            <button type="button" class="quotation-proceed-btn" onclick="quotationCart.proceedToForm()">
              Continuar con formulario
            </button>
          </div>
        </div>
      `;
      console.log(
        "✅ Modal actualizado con",
        summary.items.length,
        "productos",
      );
    }
  }

  // Renderizar item del carrito
  renderCartItem(item) {
    return `
      <div class="quotation-item" data-product-id="${item.id}">
        <div class="quotation-item-image">
          ${
            item.featured_image
              ? `<img src="${item.featured_image}" alt="${item.title}" loading="lazy">`
              : `<div class="no-image">Sin imagen</div>`
          }
        </div>
        <div class="quotation-item-details">
          <h4>${item.title}</h4>
          ${item.variant_title && !item.title.includes(item.variant_title) ? `<p class="quotation-item-variant" style="font-size: 13px; color: #333132; margin: 2px 0; font-weight: 600;">${item.variant_title}</p>` : ""}
          ${item.sku ? `<p class="quotation-item-sku" style="font-size: 12px; color: #666; margin: 2px 0;">SKU: ${item.sku}</p>` : ""}
          ${item.vendor ? `<p class="quotation-item-vendor">${item.vendor}</p>` : ""}
        </div>
        <div class="quotation-item-right">
          <div class="quotation-item-quantity">
            <label>Cantidad:</label>
            <div class="quantity-selector">
              <button type="button" class="quantity-btn minus" onclick="quotationCart.updateQuantity('${item.id}', ${item.quantity - 1})">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <input type="number" class="quantity-input" value="${item.quantity}" min="1" max="99" onchange="quotationCart.updateQuantity('${item.id}', this.value)">
              <button type="button" class="quantity-btn plus" onclick="quotationCart.updateQuantity('${item.id}', ${item.quantity + 1})">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
          <div class="quotation-item-actions">
            <button type="button" class="quotation-remove-btn" onclick="quotationCart.removeProduct('${item.id}')">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // Abrir modal del carrito
  openModal() {
    console.log("🔓 openModal() ejecutándose...");

    let modal = document.getElementById("quotation-cart-modal");

    // Si el modal no existe, crearlo dinámicamente
    if (!modal) {
      console.log("🔨 Creando modal dinámicamente...");
      modal = this.createModal();
    }

    if (modal) {
      console.log("✅ Modal encontrado/creado, actualizando contenido...");
      // PRIMERO actualizar el contenido
      this.updateModal();
      console.log("📋 Modal actualizado, abriendo...");

      // Verificar estado del modal antes de abrir
      console.log("🔍 Estado del modal antes de abrir:", {
        hasAttribute: modal.hasAttribute("open"),
        open: modal.open,
        display: window.getComputedStyle(modal).display,
        visibility: window.getComputedStyle(modal).visibility,
      });

      // LUEGO mostrar el modal si no está abierto
      if (!modal.open) {
        modal.showModal();
      }

      // Verificar estado después de abrir
      setTimeout(() => {
        console.log("🔍 Estado del modal después de abrir:", {
          hasAttribute: modal.hasAttribute("open"),
          open: modal.open,
          display: window.getComputedStyle(modal).display,
          visibility: window.getComputedStyle(modal).visibility,
        });

        // Verificar contenido del modal
        const content = modal.querySelector(".quotation-modal-content");
        if (content) {
          console.log(
            "📋 Contenido del modal:",
            content.innerHTML.length,
            "caracteres",
          );
          console.log(
            "📋 Tiene productos?",
            content.innerHTML.includes("Productos para cotizar"),
          );
          console.log(
            "📋 Tiene botón continuar?",
            content.innerHTML.includes("Continuar con formulario"),
          );
        }
      }, 100);
    } else {
      console.error("❌ No se pudo crear el modal del carrito");
    }
  }

  // Crear modal dinámicamente
  createModal() {
    const modal = document.createElement("dialog");
    modal.id = "quotation-cart-modal";
    modal.className = "quotation-cart-modal";

    modal.innerHTML = `
      <div class="quotation-modal-content">
        <!-- El contenido se genera dinámicamente con JavaScript -->
      </div>
    `;

    // Agregar al body
    document.body.appendChild(modal);
    console.log("✅ Modal creado y agregado al body");

    return modal;
  }

  // Cerrar modal
  closeModal() {
    const modal = document.getElementById("quotation-cart-modal");
    if (modal) {
      modal.close();
    }
  }

  // Proceder al formulario
  proceedToForm() {
    this.closeModal();
    // Abrir modal del formulario con los productos pre-cargados
    this.openQuotationForm();
  }

  // Abrir formulario de cotización con productos
  openQuotationForm() {
    console.log("🔓 openQuotationForm() ejecutándose...");
    console.log("📦 Carrito actual:", this.cart);
    console.log("📋 Total items:", this.getTotalItems());

    // Copargo QA3: matar cualquier cierre pendiente de un envío anterior.
    // Si no, ese timer huérfano resetea y cierra ESTE modal a mitad de uso.
    if (typeof window.cancelQuotationSuccessReset === "function") {
      window.cancelQuotationSuccessReset();
    }

    let modal = document.querySelector("#modal-quotation-form");

    // Si el modal no existe, crearlo dinámicamente
    if (!modal) {
      console.log("🔨 Creando modal del formulario dinámicamente...");
      modal = this.createQuotationFormModal();
    }

    if (modal) {
      console.log(
        "✅ Modal del formulario encontrado/creado, actualizando formulario...",
      );
      // Copargo QA2: vincular la limpieza al cerrar, venga el modal de Liquid
      // o creado por JS. Es idempotente.
      this.bindQuotationFormClose(modal);
      // Copargo QA2: partir siempre de un formulario limpio. updateQuotationForm()
      // regenera el resumen, pero no limpia mensajes ni campos de la vez anterior.
      this.resetQuotationForm();
      // Actualizar formulario con productos
      this.updateQuotationForm();
      modal.showModal();
    } else {
      console.log("❌ Modal #modal-quotation-form no encontrado");
    }
  }

  // Crear modal del formulario dinámicamente
  createQuotationFormModal() {
    const modal = document.createElement("dialog");
    modal.id = "modal-quotation-form";
    modal.className =
      "hdt-dialog hdt-dialog-modal hdt-product__modal hdt-s-gradient";
    modal.setAttribute("scroll-lock", "");

    modal.innerHTML = `
      <div class="hdt-dialog-modal__header">
        <h3 class="hdt-product__modal-title">Solicitar Cotización</h3>
        <button
          class="hdt-s-text"
          aria-controls="modal-quotation-form"
          aria-label="Cerrar"
          onclick="this.closest('dialog').close()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="hdt-dialog-modal__inner hdt-current-scrollbar">
        <p>Completa tus datos y te enviaremos la cotización para todos los productos seleccionados.</p>
        <p>Te responderemos con una propuesta clara, rápida y ajustada a tu proyecto.</p>
        <form method="post" action="/contact#QuotationForm" id="QuotationForm" class="hdt-quotation-form">
          <div class="hdt-quotation-form__grid">
            <div class="hdt-quotation-form__field">
              <label for="QuotationForm-name">Tu nombre*</label>
              <input
                type="text"
                id="QuotationForm-name"
                name="contact[nombre]"
                required
                placeholder="Ej: Ana Perez"
                maxlength="100"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field">
              <label for="QuotationForm-phone">Teléfono*</label>
              <input
                type="tel"
                id="QuotationForm-phone"
                name="contact[telefono]"
                required
                placeholder="56 9 9982 1304"
                maxlength="20"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field hdt-quotation-form__full">
              <label for="QuotationForm-email">Email*</label>
              <input
                type="email"
                id="QuotationForm-email"
                name="contact[correo]"
                required
                placeholder="Tu correo electrónico"
                maxlength="255"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field">
              <label for="QuotationForm-razon">Razón social*</label>
              <input
                type="text"
                id="QuotationForm-razon"
                name="contact[razon_social]"
                required
                placeholder="Nombre de razón social"
                maxlength="100"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field">
              <label for="QuotationForm-rut">RUT de razón social*</label>
              <input
                type="text"
                id="QuotationForm-rut"
                name="contact[rut]"
                required
                placeholder="Ej: 12.345.678-9"
                maxlength="20"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field hdt-quotation-form__full">
              <label for="QuotationForm-direccion">Dirección*</label>
              <input
                type="text"
                id="QuotationForm-direccion"
                name="contact[direccion]"
                required
                placeholder="Escribe la dirección"
                maxlength="200"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field">
              <label for="QuotationForm-giro">Giro comercial*</label>
              <input
                type="text"
                id="QuotationForm-giro"
                name="contact[giro]"
                required
                placeholder="Tu giro comercial"
                maxlength="100"
                class="hdt-quotation-form__input"
              >
            </div>
            <div class="hdt-quotation-form__field hdt-quotation-form__full">
              <button type="submit" class="hdt-btn hdt-btn-solid hdt-btn-submit-quotation hdt-font-semibold">
                Enviar solicitud de cotización
              </button>
            </div>
          </div>
        </form>
      </div>
    `;

    // Agregar al body
    document.body.appendChild(modal);
    console.log("✅ Modal del formulario creado y agregado al body");

    return modal;
  }

  // ============================================
  // Copargo QA2: limpiar al cerrar el modal
  // --------------------------------------------
  // Se vincula desde openQuotationForm(), NO desde createQuotationFormModal():
  // en las páginas donde btn-cotizacion.liquid ya imprime #modal-quotation-form
  // (la landing B2B, por ejemplo) el modal nunca se crea por JS, así que un
  // listener puesto en el constructor dinámico no se ataría nunca.
  // El flag evita duplicar el listener entre aperturas.
  // ============================================
  bindQuotationFormClose(modal) {
    if (!modal || modal.dataset.copargoCloseBound === "1") return;
    modal.dataset.copargoCloseBound = "1";

    modal.addEventListener("close", () => {
      // Un cierre manual invalida el cierre automático pendiente del envío
      if (typeof window.cancelQuotationSuccessReset === "function") {
        window.cancelQuotationSuccessReset();
      }
      this.resetQuotationForm();
    });

    console.log("🔗 Limpieza al cerrar vinculada al modal del formulario");
  }

  // ============================================
  // Copargo QA2: limpieza total del formulario de cotización
  // --------------------------------------------
  // Antes solo se llamaba form.reset(), que limpia los <input> pero NO
  // toca los nodos que el JS inyecta. Quedaban colgados:
  //   - .quotation-summary-container -> los productos ya cotizados
  //   - .quotation-message-container -> el mensaje de éxito/error anterior
  //   - input[name="contact[products]"] -> el JSON de la cotización vieja
  // Por eso al abrir "Cotizar" en otra tarjeta se veía el producto anterior.
  //
  // Se llama en tres momentos, para que el estado quede limpio pase lo que
  // pase con el timing: al ABRIR el formulario, al CERRARLO, y tras un
  // envío exitoso (desde quotation-emailjs.js).
  // ============================================
  resetQuotationForm(form) {
    const target =
      form ||
      document.querySelector("#modal-quotation-form .hdt-quotation-form") ||
      document.querySelector(".hdt-quotation-form");

    if (!target) return;

    // 1. Campos del formulario
    target.reset();

    // 2. Limpiar estilos de validación que quedaron marcados en rojo
    target.querySelectorAll("[required]").forEach((field) => {
      field.style.borderColor = "";
      field.style.backgroundColor = "";
      delete field.dataset.errorMsg;
    });

    // 3. Resumen de productos inyectado por updateQuotationForm()
    const summary = target.querySelector(".quotation-summary-container");
    if (summary) summary.innerHTML = "";

    // 4. Mensaje de éxito/error inyectado por showMessage() (fallback inline)
    const message = target.querySelector(".quotation-message-container");
    if (message) message.innerHTML = "";

    // 5. Payload de productos: form.reset() lo deja en su defaultValue, pero
    //    lo forzamos por si el valor se seteó como atributo.
    const productsField = target.querySelector(
      'input[name="contact[products]"]',
    );
    if (productsField) productsField.value = "";

    console.log("🧹 Formulario de cotización reiniciado");
  }

  // Actualizar formulario de cotización con EmailJS
  updateQuotationForm() {
    console.log("📋 updateQuotationForm() ejecutándose...");

    // Buscar el formulario dentro del modal de cotización
    const modal = document.getElementById("modal-quotation-form");
    console.log("📋 Modal encontrado:", modal ? "✅" : "❌");

    let form = null;
    if (modal) {
      form = modal.querySelector(".hdt-quotation-form");
      console.log("📋 Formulario dentro del modal:", form ? "✅" : "❌");
    }

    // Si no está en el modal, buscar en todo el documento (fallback para formularios estáticos)
    if (!form) {
      form = document.querySelector(".hdt-quotation-form");
      console.log("📋 Formulario en documento (fallback):", form ? "✅" : "❌");
    }

    console.log("📋 Formulario final encontrado:", form ? "✅" : "❌");

    if (!form) {
      console.log("❌ No se encontró formulario .hdt-quotation-form");
      return;
    }

    // Generar resumen del carrito
    const summary = this.getCartSummary();
    console.log("📦 Resumen generado:", summary);
    console.log("📋 Número de items en resumen:", summary.items.length);

    // Agregar campo oculto con productos
    let productsField = form.querySelector('input[name="contact[products]"]');
    if (!productsField) {
      productsField = document.createElement("input");
      productsField.type = "hidden";
      productsField.name = "contact[products]";
      form.appendChild(productsField);
      console.log("✅ Campo contact[products] creado");
    }

    productsField.value = JSON.stringify(summary);
    console.log("📦 Valor del campo contact[products]:", productsField.value);

    // Crear o actualizar el resumen de productos en el formulario
    let summaryContainer = form.querySelector(".quotation-summary-container");

    if (!summaryContainer) {
      summaryContainer = document.createElement("div");
      summaryContainer.className = "quotation-summary-container";
      console.log("✅ Contenedor .quotation-summary-container creado");

      // Insertar antes del formulario principal
      const formGrid = form.querySelector(".hdt-quotation-form__grid");
      if (formGrid) {
        formGrid.parentNode.insertBefore(summaryContainer, formGrid);
        console.log("✅ Contenedor insertado antes del form grid");
      } else {
        form.appendChild(summaryContainer);
        console.log("✅ Contenedor agregado al formulario");
      }
    } else {
      console.log("✅ Contenedor .quotation-summary-container ya existía");
    }

    // Generar HTML del resumen - estilo main cart
    const summaryHTML = `
      <div class="quotation-summary">
        <h4 class="quotation-summary-title">Resumen de cotización</h4>
        <div class="quotation-summary-items">
          ${summary.items
            .map(
              (item) => `
            <div class="summary-item">
              <div class="summary-item-info">
                ${
                  item.featured_image
                    ? `<img src="${item.featured_image}" alt="${item.title}" class="summary-item-image">`
                    : `<div class="summary-item-placeholder">Sin imagen</div>`
                }
                <div class="summary-item-details">
                  <strong>${item.title}</strong>
                  ${item.sku ? `<div style="font-size: 11px; color: #666;">SKU: ${item.sku}</div>` : ""}
                  <span class="summary-item-quantity">Cantidad: ${item.quantity}</span>
                </div>
              </div>
            </div>
          `,
            )
            .join("")}
        </div>
        <div class="quotation-summary-total">
          <span class="summary-total-label">Total de productos:</span>
          <span class="summary-total-value">${summary.total_items}</span>
        </div>
      </div>
    `;

    summaryContainer.innerHTML = summaryHTML;
    console.log("✅ HTML del resumen generado y actualizado");
    console.log("📋 Items en el HTML:", summary.items.length);

    // Forzar visibilidad del resumen con estilos inline
    setTimeout(() => {
      const summaryDiv = summaryContainer.querySelector(".quotation-summary");
      if (summaryDiv) {
        // Forzar estilos inline para asegurar visibilidad
        summaryDiv.style.display = "block";
        summaryDiv.style.visibility = "visible";
        summaryDiv.style.opacity = "1";
        summaryDiv.style.height = "auto";
        summaryDiv.style.position = "static";
        summaryDiv.style.marginTop = "20px";
        summaryDiv.style.padding = "15px";
        summaryDiv.style.border = "1px solid #e0e0e0";
        summaryDiv.style.borderRadius = "8px";
        summaryDiv.style.backgroundColor = "#f9f9f9";

        console.log("🔍 Estilos inline aplicados al resumen");
        console.log("📋 Estilos después de forzar:", {
          display: summaryDiv.style.display,
          visibility: summaryDiv.style.visibility,
          opacity: summaryDiv.style.opacity,
          height: summaryDiv.style.height,
          marginTop: summaryDiv.style.marginTop,
        });

        // Verificar items individuales
        const items = summaryDiv.querySelectorAll(".summary-item");
        console.log("📋 Items encontrados en DOM:", items.length);
        items.forEach((item, index) => {
          item.style.display = "flex";
          item.style.alignItems = "center";
          item.style.padding = "10px 0";
          item.style.borderBottom = "1px solid #eee";
          console.log(`📋 Item ${index}: estilos aplicados`);
        });
      } else {
        console.log("❌ No se encontró el div .quotation-summary");
      }
    }, 100);

    // Configurar EmailJS para el formulario
    this.setupEmailJSForm(form);
    console.log("✅ Formulario actualizado completamente");
  }

  // Configurar EmailJS para el formulario de cotización
  setupEmailJSForm(form) {
    console.log("🔧 setupEmailJSForm() ejecutándose...");
    console.log("📋 Formulario recibido:", form);
    console.log("📋 ID del formulario:", form.id);
    console.log("📋 Clases del formulario:", form.className);
    console.log(
      "📋 ¿Tiene campos required?",
      form.querySelectorAll("[required]").length,
    );

    // NO clonar el formulario para evitar perder datos
    // Simplemente asegurar que el campo de productos esté actualizado

    // Agregar los productos del carrito como campo oculto (si no existe)
    let productsField = form.querySelector('input[name="contact[products]"]');
    if (!productsField) {
      productsField = document.createElement("input");
      productsField.type = "hidden";
      productsField.name = "contact[products]";
      form.appendChild(productsField);
      console.log("✅ Campo contact[products] creado");
    }

    // Actualizar el valor del campo con los datos más recientes
    const summary = this.getCartSummary();
    productsField.value = JSON.stringify(summary);
    console.log("📦 Campo contact[products] actualizado:", productsField.value);

    // Agregar validación personalizada antes del envío
    console.log("🔧 Agregando event listener de validación al formulario...");
    console.log("📋 Formulario antes de agregar listener:", form);

    // Copargo: el listener de validación se vincula UNA sola vez por
    // formulario. Antes se agregaba en cada apertura del modal con una
    // arrow anónima (no removible), así que tras N aperturas la validación
    // corría N veces sobre el mismo submit.
    if (form.dataset.copargoValidationBound === "1") {
      console.log("↩️ Validación ya vinculada, no se duplica el listener");
    } else {
      form.dataset.copargoValidationBound = "1";
      form.addEventListener(
        "submit",
        (e) => {
          console.log("🚨 MI VALIDACIÓN SE ESTÁ EJECUTANDO!!!");
          console.log("🔍 Validando formulario antes de envío...");
          console.log("📋 Event target:", e.target);
          console.log("📋 Formulario actual:", form);

          // Obtener todos los campos requeridos
          const requiredFields = form.querySelectorAll("[required]");
          console.log("📋 Campos requeridos encontrados:", requiredFields.length);

          let isValid = true;
          let firstInvalidField = null;

          requiredFields.forEach((field, index) => {
            console.log(
              `🔍 Validando campo ${index}: ${field.name} - Valor: "${field.value}"`,
            );

            // Limpiar validaciones anteriores
            field.style.borderColor = "";
            field.style.backgroundColor = "";

            // Validar campo
            if (!field.value.trim()) {
              console.log(`❌ Campo vacío: ${field.name}`);
              field.style.borderColor = "#ff4444";
              field.style.backgroundColor = "#fff5f5";
              isValid = false;

              if (!firstInvalidField) {
                firstInvalidField = field;
              }
            } else if (field.type === "email") {
              // Validación específica para email
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(field.value.trim())) {
                console.log(`❌ Email inválido: ${field.value}`);
                field.style.borderColor = "#ff4444";
                field.style.backgroundColor = "#fff5f5";
                isValid = false;

                if (!firstInvalidField) {
                  firstInvalidField = field;
                }

                const fieldLabel =
                  field.previousElementSibling?.textContent?.replace("*", "") ||
                  "Email";
                field.dataset.errorMsg = `${fieldLabel}: Por favor, ingresa un correo válido.`;
              }
            } else if (field.name === "contact[telefono]") {
              // Validación específica para teléfono (debe tener al menos 8 dígitos)
              const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/;
              if (!phoneRegex.test(field.value.trim())) {
                console.log(`❌ Teléfono inválido: ${field.value}`);
                field.style.borderColor = "#ff4444";
                field.style.backgroundColor = "#fff5f5";
                isValid = false;

                if (!firstInvalidField) {
                  firstInvalidField = field;
                }

                const fieldLabel =
                  field.previousElementSibling?.textContent?.replace("*", "") ||
                  "Teléfono";
                field.dataset.errorMsg = `${fieldLabel}: El número debe tener al menos 8 dígitos.`;
              }
            } else if (field.name === "contact[rut]") {
              // Validación específica para RUT chileno
              // Aceptar formatos: 12345678-9, 12.345.678-9, 12 345 678-9
              const rutValue = field.value.trim();
              const cleanRut = rutValue.replace(/\./g, "").replace(/\s/g, "");
              const rutRegex = /^[0-9]{7,8}[-][0-9kK]{1}$/;

              if (!rutRegex.test(cleanRut)) {
                console.log(`❌ RUT inválido: ${field.value}`);
                field.style.borderColor = "#ff4444";
                field.style.backgroundColor = "#fff5f5";
                isValid = false;

                if (!firstInvalidField) {
                  firstInvalidField = field;
                }

                // Guardar mensaje de error específico para RUT en el dataset para mostrarlo al final
                const fieldName =
                  field.previousElementSibling?.textContent?.replace("*", "") ||
                  "Campo";
                field.dataset.errorMsg = `${fieldName}: Formato inválido. Ejemplos válidos: 12345678-9 o 12.345.678-K`;
              }
            }
          });

          // Validar que haya productos en el carrito
          console.log("📦 Carrito actual:", this.cart);
          if (this.cart.length === 0) {
            console.log("❌ No hay productos en el carrito de cotización");
            isValid = false;
            this.showNotification(
              "Debes agregar al menos un producto para cotizar",
              "error",
            );
          }

          console.log("📋 ¿Formulario válido?", isValid);

          if (!isValid) {
            console.log("❌ Formulario inválido, cancelando envío");
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation(); // Evitar que otros listeners se ejecuten

            // Enfocar el primer campo inválido
            if (firstInvalidField) {
              console.log("📍 Enfocando campo inválido:", firstInvalidField.name);
              firstInvalidField.focus();
              firstInvalidField.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });

              // Mostrar mensaje de error (específico si existe, sino genérico)
              const fieldName =
                firstInvalidField.previousElementSibling?.textContent?.replace(
                  "*",
                  "",
                ) || "Campo";

              const errorMsg =
                firstInvalidField.dataset.errorMsg ||
                `Por favor, completa el campo: ${fieldName}`;

              this.showNotification(errorMsg, "error");
            }

            return false;
          }

          console.log("✅ Formulario válido, permitiendo envío");
        },
        { capture: true },
      ); // Ejecutar antes que otros listeners
      console.log("✅ Event listener de validación agregado");
    }

    // Link manual con EmailJS Global Handler
    if (typeof window.handleQuotationSubmit === "function") {
      console.log("🔗 Vinculando handleQuotationSubmit al formulario dinámico");
      // Remover listener anterior si existe para evitar duplicados
      form.removeEventListener("submit", window.handleQuotationSubmit);
      // Agregar listener principal
      form.addEventListener("submit", window.handleQuotationSubmit);
    } else {
      console.error("⚠️ window.handleQuotationSubmit no está disponible");
    }

    console.log("✅ Formulario configurado para EmailJS sin perder datos");
  }

  // Mostrar notificación
  showNotification(message, type = "info") {
    // Crear elemento de notificación
    const notification = document.createElement("div");
    notification.className = `quotation-notification quotation-${type}`;
    notification.textContent = message;

    // Agregar al DOM
    document.body.appendChild(notification);

    // Animación de entrada
    setTimeout(() => notification.classList.add("show"), 10);

    // Remover después de 5 segundos
    setTimeout(() => {
      notification.classList.remove("show");
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  // Vincular eventos
  bindEvents() {
    // Eventos para botones de cotización
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".cotizacion-btn-main");
      if (btn) {
        e.preventDefault();
        console.log("🖱️ Click detectado en .cotizacion-btn-main");

        // Si el botón ya tiene la clase 'has-items' (Ver cotización), solo abrir modal
        if (btn.classList.contains("has-items")) {
          console.log(
            "📋 El producto ya está en el carrito, abriendo modal...",
          );
          this.openModal();
          return;
        }

        // Obtener datos del producto
        const productData = this.getProductDataFromButton(btn);

        if (productData) {
          // SEGUNDA COMPROBACIÓN: Si el producto ya está en el carrito, solo abrir modal (esto ayuda si el UI no se actualizó a tiempo)
          const isAlreadyInCart = this.cart.some(
            (item) => String(item.id) === String(productData.id),
          );
          if (isAlreadyInCart) {
            console.log(
              "Safeguard: El producto ya está en el carrito, abriendo modal...",
            );
            this.openModal();
            return;
          }

          // Agregar producto al carrito
          const added = this.addProduct(productData);

          // Si se agregó correctamente, abrir el modal del carrito
          if (added) {
            setTimeout(() => {
              this.openModal();
            }, 300);
          }
        } else {
          this.showNotification("Error al obtener datos del producto", "error");
        }
      }

      // Manejar botón "cotizar al mayor"
      const cotizarMayorBtn = e.target.closest("[id^='cotizar-mayor-']");
      if (cotizarMayorBtn) {
        e.preventDefault();
        e.stopPropagation();

        // Obtener el ID del producto del ID del botón
        const productId = cotizarMayorBtn.id.replace("cotizar-mayor-", "");

        // Buscar el objeto product en el contexto global o crear un objeto básico
        let productData = null;

        // Intentar obtener del script del producto si está disponible
        const productScript =
          document.querySelector(`[data-product-id="${productId}"]`) ||
          document.querySelector("#product-json-" + productId) ||
          document.querySelector('.product-json[type="application/json"]') ||
          document.querySelector("[data-product-json]");

        if (productScript) {
          try {
            productData = JSON.parse(productScript.textContent);
          } catch (e) {
            console.error("Error parsing product data:", e);
          }
        }

        // Si no encontramos el producto, intentar obtener de window.product o variables globales
        if (!productData && window.product) {
          productData = window.product;
        }

        // Si todavía no encontramos, crear un objeto básico con los datos del DOM
        if (!productData) {
          const title =
            document.querySelector("h1")?.textContent ||
            document.querySelector(".product__title")?.textContent ||
            document.querySelector("[data-product-title]")?.textContent ||
            "Producto";

          const price =
            document.querySelector(".price__regular .money")?.textContent ||
            document.querySelector(".price-item--regular .money")
              ?.textContent ||
            "0";

          const vendor =
            document.querySelector(".product__vendor")?.textContent ||
            document.querySelector(".product-vendor")?.textContent ||
            "";

          // Buscar imagen del producto principal
          let featuredImage = "";

          // Primero buscar en contenedores específicos del producto
          const productContainers = [
            ".product__media-wrapper",
            ".product-media-wrapper",
            ".product__media",
            ".product-media",
            ".main-product",
            ".product-main",
          ];

          let productContainer = null;
          for (const containerSelector of productContainers) {
            const container = document.querySelector(containerSelector);
            if (container) {
              productContainer = container;
              console.log(
                "✅ Contenedor de producto encontrado:",
                containerSelector,
              );
              break;
            }
          }

          // Selectores específicos para imágenes del producto principal
          const imageSelectors = [
            ".product__media img",
            ".product-media img",
            ".product-gallery img",
            ".product-image img",
            ".main-product-image img",
            ".featured-image img",
            ".product-featured-image img",
          ];

          console.log("🔍 Buscando imagen del producto...");

          // Buscar dentro del contenedor del producto si lo encontramos
          const searchArea = productContainer || document.body;

          for (const selector of imageSelectors) {
            const img = searchArea.querySelector(selector);
            console.log(
              `🔍 Probando selector: ${selector}`,
              img ? "✅ Encontrado" : "❌ No encontrado",
            );
            if (img && img.src && !isLogoImage(img)) {
              featuredImage = img.src;
              console.log(
                "✅ Imagen del producto seleccionada:",
                featuredImage,
              );
              break;
            }
          }

          // Si no encontramos imagen, buscar cualquier imagen que no sea logo
          if (!featuredImage) {
            console.log("🔄 Buscando cualquier imagen que no sea logo...");
            const allImages = document.querySelectorAll("img");
            for (const img of allImages) {
              if (img.src && img.naturalWidth > 200 && !isLogoImage(img)) {
                featuredImage = img.src;
                console.log("✅ Imagen válida encontrada:", featuredImage);
                break;
              }
            }
          }

          // Función para detectar si una imagen es un logo
          function isLogoImage(img) {
            const src = img.src.toLowerCase();
            const alt = (img.alt || "").toLowerCase();
            const className = (img.className || "").toLowerCase();

            // Excluir logos por características comunes
            return (
              src.includes("logo") ||
              alt.includes("logo") ||
              className.includes("logo") ||
              img.naturalWidth < 150 ||
              img.naturalHeight < 150 ||
              img.naturalWidth / img.naturalHeight > 3 || // Muy panorámica
              img.naturalHeight / img.naturalWidth > 3 // Muy vertical
            );
          }

          productData = {
            id: parseInt(productId),
            title: title.trim(),
            handle: window.location.pathname
              .replace("/products/", "")
              .replace("/", ""),
            vendor: vendor.trim(),
            type: "",
            url: window.location.pathname,
            price: parseInt(price.replace(/[^0-9]/g, "")) || 0,
            featured_image: featuredImage,
            sku: productData ? productData.sku : "",
          };

          // Asegurar protocolo https en imagen
          if (
            productData.featured_image &&
            productData.featured_image.startsWith("//")
          ) {
            productData.featured_image = "https:" + productData.featured_image;
          }

          console.log("📦 Datos del producto creados:", productData);
        }

        // Check for selected variant override
        const productForm = cotizarMayorBtn.closest("form");
        if (productForm && productData) {
          const variantInput = productForm.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            const selectedVariantId = parseInt(variantInput.value);

            // Init variant detection variables
            let variantTitleFound = false;
            let variantImageFound = false;

            // 1. Try to get data from embedded JSON "variants" array
            // Also try window.product which is often available in Shopify themes
            const variantsSource =
              productData.variants && Array.isArray(productData.variants)
                ? productData.variants
                : window.product && window.product.variants
                  ? window.product.variants
                  : null;

            if (variantsSource) {
              const selectedVariant = variantsSource.find(
                (v) => v.id === selectedVariantId,
              );
              console.log("🔍 Variante encontrada en JSON:", selectedVariant);

              if (selectedVariant) {
                // Update core data from variant
                productData.id = selectedVariant.id;

                if (selectedVariant.price)
                  productData.price = selectedVariant.price;
                if (selectedVariant.sku) productData.sku = selectedVariant.sku;

                // Handle Title
                if (
                  selectedVariant.title &&
                  selectedVariant.title !== "Default Title" &&
                  !productData.title.includes(selectedVariant.title)
                ) {
                  productData.title = `${productData.title} - ${selectedVariant.title}`;
                  variantTitleFound = true;
                }

                // Handle Image - Prioritize variant image from JSON
                if (selectedVariant.featured_image) {
                  // featured_image can be an object or string depending on API version
                  let imgSrc =
                    selectedVariant.featured_image.src ||
                    selectedVariant.featured_image;
                  if (imgSrc) {
                    productData.featured_image = imgSrc;
                    variantImageFound = true;
                    console.log(
                      "✅ Imagen de variante obtenida de JSON:",
                      productData.featured_image,
                    );
                  }
                }
              }
            } else {
              // Update ID if JSON didn't have variants or didn't match (fallback ID update)
              if (productData.id !== selectedVariantId) {
                productData.id = selectedVariantId;
              }
            }

            // 2. DOM Scraping (runs if JSON failed to provide specific title/image OR as enrichment)
            // Look for container of the button to get access to sibling modal inputs
            const buttonContainer =
              cotizarMayorBtn.closest(".hdt-product__buy-button") ||
              productForm.parentElement;

            // Try to find the hidden input defined in the liquid file (it is outside the product form, in the modal dialog)
            let contactInput = null;
            if (buttonContainer) {
              contactInput = buttonContainer.querySelector(
                'input[name="contact[product]"]',
              );
            }
            if (!contactInput) {
              // Last ditch: try global search but this is risky if multiple products
              // contactInput = document.querySelector('input[name="contact[product]"]');
            }

            if (!variantTitleFound) {
              // A. Try DOM labels (Visual selection)
              try {
                const checkedInputs = Array.from(
                  productForm.querySelectorAll(
                    "fieldset input:checked + label",
                  ),
                );
                if (checkedInputs.length > 0) {
                  const variantName = checkedInputs
                    .map((el) => el.textContent.trim())
                    .filter((name) => name && name !== "Default Title")
                    .join(" / ");
                  if (variantName && !productData.title.includes(variantName)) {
                    productData.title = `${productData.title} - ${variantName}`;
                    variantTitleFound = true;
                    console.log(
                      "Variante detectada desde DOM (labels):",
                      variantName,
                    );
                  }
                }

                if (!variantTitleFound) {
                  const selectedOptions = Array.from(
                    productForm.querySelectorAll(
                      ".hdt-variant-picker option:checked, .hdt-select-option.selected",
                    ),
                  );
                  if (selectedOptions.length > 0) {
                    const variantName = selectedOptions
                      .map((el) => el.textContent.trim())
                      .filter((name) => name && name !== "Default Title")
                      .join(" / ");
                    if (
                      variantName &&
                      !productData.title.includes(variantName)
                    ) {
                      productData.title = `${productData.title} - ${variantName}`;
                      variantTitleFound = true;
                      console.log(
                        "Variante detectada desde DOM (options):",
                        variantName,
                      );
                    }
                  }
                }
              } catch (e) {
                console.error("Error parsing variant DOM:", e);
              }

              // B. Try hidden input fallback
              if (!variantTitleFound && contactInput && contactInput.value) {
                const parts = contactInput.value.split(" - ");
                if (parts.length >= 2) {
                  let variantDescription = parts[1];
                  // Safe scrubbing
                  variantDescription = variantDescription
                    .replace(/\(SKU:.*?\)/, "")
                    .replace(/Default Title/gi, "")
                    .trim();
                  if (variantDescription.includes("http")) {
                    variantDescription = variantDescription
                      .split("http")[0]
                      .trim();
                  }
                  if (variantDescription.endsWith("-")) {
                    variantDescription = variantDescription.slice(0, -1).trim();
                  }
                  variantDescription = variantDescription.replace(/\s+/g, " ");

                  if (
                    variantDescription &&
                    !productData.title.includes(variantDescription)
                  ) {
                    productData.title = `${productData.title} - ${variantDescription}`;
                    variantTitleFound = true;
                    console.log(
                      "Variante detectada desde input oculto:",
                      variantDescription,
                    );
                  }
                }
              }
            }

            // 3. Image Fallback - Search for the specific variant image or main active image
            if (!variantImageFound) {
              // Strategy A: Look for media items tagged with this variant ID
              const mediaItems =
                document.querySelectorAll("[data-variants-id]");
              for (const item of mediaItems) {
                const variantIds = item
                  .getAttribute("data-variants-id")
                  .split(";");
                if (variantIds.includes(String(selectedVariantId))) {
                  const img = item.querySelector("img");
                  if (img && img.src) {
                    productData.featured_image = img.src;
                    variantImageFound = true;
                    console.log(
                      "Imagen detectada desde data-variants-id:",
                      productData.featured_image,
                    );
                    break;
                  }
                }
              }

              // Strategy B: Try to find the currently SELECTED/VISIBLE image in the gallery
              if (!variantImageFound) {
                const activeSlide = document.querySelector(
                  ".hdt-slider__slide.is-selected img, .hdt-slider__slide.is-active img, .is-active .hdt-product__media img, .product-media__item.is-active img",
                );
                if (activeSlide && activeSlide.src) {
                  productData.featured_image = activeSlide.src;
                  variantImageFound = true;
                  console.log(
                    "Imagen detectada desde slide activo:",
                    productData.featured_image,
                  );
                }
              }

              // Strategy C: Try the variant picker swatches
              if (!variantImageFound) {
                const activeVariantImage = productForm.querySelector(
                  '.hdt-variant-picker [data-variant-id="' +
                    selectedVariantId +
                    '"] img, .hdt-variant-picker input[value="' +
                    selectedVariantId +
                    '"] ~ label img',
                );
                if (activeVariantImage && activeVariantImage.src) {
                  productData.featured_image = activeVariantImage.src;
                  variantImageFound = true;
                  console.log(
                    "Imagen detectada desde swatch:",
                    productData.featured_image,
                  );
                }
              }

              // Strategy D: Last resort - Re-scan main gallery
              if (!variantImageFound) {
                const mainMediaImages = document.querySelectorAll(
                  ".hdt-product-media__main img, .product-media img, .product__media img",
                );
                for (const img of mainMediaImages) {
                  if (
                    img.offsetParent !== null &&
                    img.width > 50 &&
                    img.src &&
                    !String(img.src).includes("no-image")
                  ) {
                    productData.featured_image = img.src;
                    variantImageFound = true;
                    console.log(
                      "Imagen detectada desde galería principal (fallback final):",
                      productData.featured_image,
                    );
                    break;
                  }
                }
              }

              // Strategy E: Explicit Failover to Main Product Image
              if (!variantImageFound && productData.featured_image) {
                console.log(
                  "⚠️ No se encontró imagen específica de variante, visualizando imagen principal.",
                );
                variantImageFound = true;
              }
            }

            // 4. SKU Fallback from hidden input
            if (!productData.sku && contactInput && contactInput.value) {
              const skuMatch = contactInput.value.match(/\(SKU: (.*?)\)/);
              if (skuMatch && skuMatch[1]) {
                productData.sku = skuMatch[1];
              }
            }

            // 5. Final fallback for SKU
            if (!productData.sku) {
              const skuEl = document.querySelector(
                '.product__sku, .sku, [itemprop="sku"]',
              );
              if (skuEl) {
                productData.sku = skuEl.textContent
                  .trim()
                  .replace("SKU:", "")
                  .replace("Sku:", "")
                  .trim();
                console.log(
                  "Variante SKU detectada desde DOM general:",
                  productData.sku,
                );
              }
            }

            // Extract quantity
            const qtyInput = productForm.querySelector(
              'input[name="quantity"]',
            );
            if (qtyInput) {
              productData.quantity = parseInt(qtyInput.value) || 1;
            } else {
              productData.quantity = 1;
            }
          } else {
            // In case there's no variant input, still try to find quantity
            const qtyInput = productForm.querySelector(
              'input[name="quantity"]',
            );
            if (qtyInput) {
              productData.quantity = parseInt(qtyInput.value) || 1;
            }
          }
        } else {
          // Find global quantity input if form is not found
          const qtyInput = document.querySelector('input[name="quantity"]');
          if (qtyInput) {
            productData.quantity = parseInt(qtyInput.value) || 1;
          }
        }

        const added = this.addProduct(productData);

        // Abrir el modal independientemente de si se agregó o ya existía
        setTimeout(() => {
          this.openModal();
        }, 300);
      }
    });

    // Cerrar modal con Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeModal();
      }
    });
  }

  // Obtener datos del producto desde el botón
  getProductDataFromButton(button) {
    try {
      // 1. Intentar obtener del atributo data-product (nueva implementación preferida)
      const dataProduct = button.getAttribute("data-product");
      if (dataProduct) {
        try {
          const productData = JSON.parse(dataProduct);
          if (productData && productData.id) {
            console.log("✅ Datos obtenidos de data-product:", productData);
            return productData;
          }
        } catch (err) {
          console.error("Error parsing data-product JSON:", err);
        }
      }

      // 2. Intentar obtener del atributo onclick (legacy/fallback)
      const onclick = button.getAttribute("onclick");
      if (onclick && onclick.includes("quotationCart.addProduct")) {
        // Extraer el JSON del onclick
        const match = onclick.match(/quotationCart\.addProduct\((.*)\)/);
        if (match && match[1]) {
          // Evaluar el JSON de forma segura
          const productJson = match[1].replace(/'/g, '"');
          const productData = JSON.parse(productJson);

          // Si el onclick tiene datos completos (id y sku), usarlo como fuente de verdad
          if (productData.id && productData.sku) {
            console.log(
              "✅ Datos completos desde onclick (card):",
              productData,
            );
            return productData;
          }

          // Si tiene ID pero no SKU, intentar complementar desde la página
          if (productData.id) {
            console.log(
              "⚠️ Onclick tiene ID pero falta SKU, complementando...",
            );
            const pageData = this.getProductDataFromPage(button);
            // Preservar datos del onclick, solo complementar lo que falta
            return {
              id: productData.id, // Preservar ID del onclick
              title: productData.title || pageData?.title || "Producto",
              handle: productData.handle || pageData?.handle || "producto",
              url: productData.url || pageData?.url || "#",
              price: productData.price || pageData?.price || 0,
              compare_at_price:
                productData.compare_at_price ||
                pageData?.compare_at_price ||
                null,
              featured_image:
                productData.featured_image || pageData?.featured_image || null,
              vendor: productData.vendor || pageData?.vendor || null,
              type: productData.type || pageData?.type || null,
              tags: productData.tags || pageData?.tags || [],
              sku: productData.sku || pageData?.sku || "", // Preservar SKU del onclick si existe
            };
          }
        }
      }

      // Si no funciona o está incompleto, intentar obtener de los datos del producto en la página
      return this.getProductDataFromPage(button);
    } catch (error) {
      console.error("Error getting product data:", error);
      return null;
    }
  }

  // Obtener datos del producto desde la página
  getProductDataFromPage(button) {
    // Buscar la tarjeta de producto más cercana
    const card = button.closest(".hdt-card-product");
    if (!card) {
      return null;
    }

    // Obtener datos del producto de la tarjeta
    const titleElement = card.querySelector(".hdt-card-product__title");
    const imageElement = card.querySelector(".hdt-card-product__media img");
    const priceElement = card.querySelector(".hdt-card-product__price");
    const linkElement = card.querySelector("[data-pr-url]");

    // Construir objeto de producto
    const product = {
      id: this.generateProductId(card),
      title: titleElement ? titleElement.textContent.trim() : "Producto",
      handle: linkElement
        ? linkElement.getAttribute("href").replace("/", "")
        : "producto",
      url: linkElement ? linkElement.getAttribute("href") : "#",
      price: this.extractPrice(priceElement),
      compare_at_price: null,
      featured_image: imageElement ? imageElement.getAttribute("src") : null,
      vendor: null,
      type: null,
      tags: [],
      sku: this.extractSku(card),
    };

    // Asegurar que la imagen tenga protocolo https
    if (product.featured_image && product.featured_image.startsWith("//")) {
      product.featured_image = "https:" + product.featured_image;
    }

    return product;
  }

  // Extraer SKU de la tarjeta
  extractSku(card) {
    // 1. Intentar encontrar SKU en elementos visibles o data attributes
    const skuElement = card.querySelector(
      ".hdt-card-product__sku, [data-product-sku], .sku, [itemprop='sku']",
    );
    if (skuElement) {
      const sku =
        skuElement.textContent.trim() ||
        skuElement.getAttribute("data-product-sku");
      if (sku) return sku.replace("SKU:", "").trim();
    }

    // 2. Buscar en el JSON del producto embebido (para obtener SKU de la variante principal)
    try {
      const productLink = card.querySelector("a[href*='/products/']");
      if (productLink) {
        const productHandle = productLink
          .getAttribute("href")
          .split("/products/")[1]
          ?.split("?")[0];
        if (productHandle) {
          // Buscar el JSON del producto en scripts de la página
          const scripts = document.querySelectorAll(
            'script[type="application/json"]',
          );
          for (const script of scripts) {
            try {
              const data = JSON.parse(script.textContent);
              if (data.handle === productHandle || data.id) {
                // Si hay variantes, usar el SKU de la primera variante disponible
                if (data.variants && data.variants.length > 0) {
                  const firstAvailable =
                    data.variants.find((v) => v.available) || data.variants[0];
                  if (firstAvailable.sku) return firstAvailable.sku;
                }
                // Si no hay variantes, usar el SKU del producto principal
                if (data.sku) return data.sku;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (e) {
      console.warn("Error extracting SKU from card:", e);
    }

    // Si no se encuentra, devolver cadena vacía
    return "";
  }

  // Generar ID único para el producto
  generateProductId(card) {
    try {
      // 1. Intentar obtener el ID real del producto desde el JSON
      const productLink = card.querySelector("a[href*='/products/']");
      if (productLink) {
        const productHandle = productLink
          .getAttribute("href")
          .split("/products/")[1]
          ?.split("?")[0];
        if (productHandle) {
          // Buscar el JSON del producto en scripts de la página
          const scripts = document.querySelectorAll(
            'script[type="application/json"]',
          );
          for (const script of scripts) {
            try {
              const data = JSON.parse(script.textContent);
              if (data.handle === productHandle || data.id) {
                // Si hay variantes, usar el ID de la primera variante disponible
                if (data.variants && data.variants.length > 0) {
                  const firstAvailable =
                    data.variants.find((v) => v.available) || data.variants[0];
                  if (firstAvailable.id) {
                    console.log(
                      "✅ ID de variante desde card:",
                      firstAvailable.id,
                    );
                    return firstAvailable.id;
                  }
                }
                // Si no hay variantes, usar el ID del producto principal
                if (data.id) {
                  console.log("✅ ID de producto desde card:", data.id);
                  return data.id;
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      // 2. Fallback: Usar el título y URL para generar un ID consistente
      const title = card.querySelector(".hdt-card-product__title");
      const link = card.querySelector("[data-pr-url]");
      const idString = `${title ? title.textContent.trim() : ""}-${link ? link.getAttribute("href") : ""}`;

      // Método seguro para generar ID sin caracteres especiales
      return this.hashString(idString);
    } catch (error) {
      console.error("Error generating product ID:", error);
      // Fallback: usar timestamp y random
      return (
        "product_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
      );
    }
  }

  // Método seguro para hashear strings con caracteres especiales
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Extraer precio del elemento
  extractPrice(priceElement) {
    if (!priceElement) return "0";

    const priceText = priceElement.textContent.trim();
    // Remover símbolos de moneda y puntos
    const cleanPrice = priceText.replace(/[^\d]/g, "");
    return cleanPrice || "0";
  }
}

// Inicializar el sistema
// Inicializar el sistema
let quotationCart;
document.addEventListener("DOMContentLoaded", () => {
  quotationCart = new QuotationCart();
  // Hacer disponible globalmente
  window.quotationCart = quotationCart;
});
